import { useApi } from "@backstage/core-plugin-api";
import {
  EXTRANEOUS_RESPONDERS,
  opsgenieApiRef,
  OpsgenieIncident,
} from "@internal/plugin-opsgenie-api";
import { useCallback, useEffect } from "react";
import { useAsyncFn } from "react-use";

export interface Props {
  incidents: OpsgenieIncident[];
}

type Responder = OpsgenieIncident["responders"][0];

export const useParsedIncidents = ({ incidents }: Props) => {
  const opsgenieApi = useApi(opsgenieApiRef);

  const getParsedIncidents = useCallback(async () => {
    if (!incidents || incidents.length === 0) {
      return [];
    }

    // @TODO: This needs to be refactored a bit because
    // sometimes it sends a blank name. The !(tn) check is
    // fixing it for now.
    return await Promise.all(
      incidents.map(async (incident: OpsgenieIncident) => {
        const responders = await Promise.all(
          incident.responders.map(async (responder: Responder) => {
            const holder = {
              name: "",
              id: responder.id,
              filter: true,
              type: responder.type,
              link: "",
              teams: [""],
            };

            if (EXTRANEOUS_RESPONDERS.includes(responder.id)) {
              holder.filter = true;
              // eslint-disable-next-line no-param-reassign
              responder = holder;
              return holder;
            }

            if (responder.type === "team") {
              const tn = await opsgenieApi.getTeamNameById(responder.id);
              if (!tn) {
                holder.name = "Unknown Team";
                holder.filter = true;
                holder.link = "/catalog?filters[kind]=team&filters[team]=all";
                return holder;
              }
              holder.name = tn;
              holder.filter = false;
              holder.teams = [tn];
              holder.link = `/catalog/default/group/${holder.name}`;
            } else if (responder.type === "user") {
              const user = await opsgenieApi.getUser(responder.id);

              if (user) {
                holder.name = user?.fullName;
                holder.filter = false;
                holder.link = `/catalog/default/user/${
                  user?.username.split("@")[0]
                }`;
                holder.teams = responder.teams || [];
              } else {
                holder.name = "Unknown User";
                holder.filter = true;
                holder.link = "/catalog?filters[kind]=user&filters[user]=all";
              }
            }

            return holder;
          })
        );

        incident.responders = responders.filter(
          (r: Responder | undefined) => r
        );

        return incident;
      })
    );
  }, [incidents, opsgenieApi]);

  const [state, fetchParsedIncidents] = useAsyncFn(async () => {
    return await getParsedIncidents();
  }, [getParsedIncidents]);

  useEffect(() => {
    fetchParsedIncidents();
  }, [fetchParsedIncidents]);

  return {
    loading: state.loading,
    error: state.error,
    value: state?.value,
    fetchParsedIncidents,
  };
};
